import type { CampaignConfig, SpinWheelCopy, SpinWheelSegment, SpinWheelTheme } from './types';

export const defaultTheme: SpinWheelTheme = {
  primaryColor: '#0f766e',
  accentColor: '#f59e0b',
  textColor: '#172026',
  mutedTextColor: '#64748b',
  surfaceColor: '#ffffff',
  overlayColor: 'rgba(15, 23, 42, 0.58)',
  borderColor: '#d8e1e8',
  pointerColor: '#ef4444',
  buttonTextColor: '#ffffff',
  launcherColors: ['#0f766e', '#f59e0b', '#2563eb'],
  launcherPointerColor: '#ef4444',
  launcherDividerColor: '#ffffff',
  launcherTextColor: '#ffffff',
};

export const defaultCopy: SpinWheelCopy = {
  title: 'Spin for your offer',
  subtitle: "Enter your details and spin once to reveal today's reward.",
  emailPlaceholder: 'Email address',
  phonePlaceholder: 'Phone number',
  consentLabel: 'I agree to receive offers from this store.',
  submitLabel: 'Spin now',
  spinningLabel: 'Finding your reward...',
  successTitle: 'Your offer is ready',
  couponLabel: 'Coupon code',
  closeLabel: 'Close',
  triggerLabel: 'Spin to win',
  unavailableTitle: 'Campaign unavailable',
  unavailableMessage: 'This promotion is not active right now.',
};

export const defaultSegments: SpinWheelSegment[] = [
  { id: 'ten-percent', label: '10% off', color: '#0f766e', textColor: '#ffffff' },
  { id: 'free-ship', label: 'Free ship', color: '#f8fafc', textColor: '#172026' },
  { id: 'fifteen-percent', label: '15% off', color: '#f59e0b', textColor: '#172026' },
  { id: 'bonus-gift', label: 'Gift', color: '#fee2e2', textColor: '#172026' },
  { id: 'twenty-percent', label: '20% off', color: '#2563eb', textColor: '#ffffff' },
  { id: 'try-again', label: 'Try later', color: '#e2e8f0', textColor: '#172026' },
];

export const fallbackCampaignConfig: CampaignConfig = {
  campaignId: 'preview',
  shopDomain: '',
  active: true,
  segments: defaultSegments,
  theme: defaultTheme,
  copy: defaultCopy,
  trigger: {
    mode: 'delay',
    delayMs: 3500,
    exitIntentEnabled: true,
    showOncePerDays: 7,
    mobileScrollPercent: 60,
  },
  form: {
    requiredFields: ['email'],
    consentRequired: true,
  },
  plugins: {
    customCursor: {
      active: false,
    },
    announcementBar: {
      active: false,
      title: 'Limited offer',
      subtitle: 'Unlock a special reward today.',
      backgroundColor: '#111827',
      backgroundEffect: 'soft',
      textColor: '#ffffff',
      alignment: 'center',
      behavior: 'fixed',
      placement: 'top',
      ctaEnabled: false,
      ctaText: 'Shop now',
      ctaTextColor: '#111827',
      ctaBackgroundColor: '#ffffff',
      ctaCornerRadius: 50,
      ctaBorderSize: 0,
      ctaBorderColor: '#ffffff',
      dismissEnabled: false,
      dismissOnce: false,
      countdownEnabled: false,
      showCountdown: true,
    },
    announcementBarSettings: {
      allowMultiple: false,
      displayMode: 'stacked',
      rotationSeconds: 6,
      transition: 'slide',
    },
    currencyConverter: {
      active: false,
      baseCurrency: 'VND',
      targetCurrencies: ['USD', 'EUR', 'JPY', 'INR', 'CNY', 'IDR', 'THB', 'KRW', 'MYR'],
      autoConvertByIp: true,
      showCurrencySwitcher: false,
      showSwitcherOnlyOutsideVietnam: false,
      switcherPosition: 'bottom-right',
      switcherPrimaryColor: '#0f766e',
      showCurrencySymbol: true,
      showCurrencyCode: true,
      rates: {
        USD: 0.000039,
        EUR: 0.000036,
        JPY: 0.0061,
        INR: 0.0033,
        CNY: 0.00028,
        IDR: 0.62,
        THB: 0.00135,
        KRW: 0.052,
        MYR: 0.00016,
      },
    },
    shoppableVideos: {
      id: 'shoppable-1',
      active: false,
      title: 'Watch and buy',
      pageUrl: '/',
      cardCornerRadius: 20,
      cardBorderSize: 0,
      cardBorderColor: '#111827',
      cardPlaceholderColor: '#111827',
      productBarPrimaryColor: '#000000',
      productBarSecondaryColor: '#4b5563',
      productBarBackgroundColor: '#ffffff',
      placement: { preset: 'after-hero', customSelector: '', customMode: 'after' },
      items: [],
      carousels: [],
      activeCarouselId: '',
    },
    productCountdown: {
      items: [],
      activeItemId: '',
    },
  },
};

export function mergeCampaignConfig(config: Partial<CampaignConfig>): CampaignConfig {
  return {
    ...fallbackCampaignConfig,
    ...config,
    segments: config.segments?.length ? config.segments : fallbackCampaignConfig.segments,
    theme: { ...fallbackCampaignConfig.theme, ...config.theme },
    copy: { ...fallbackCampaignConfig.copy, ...config.copy },
    trigger: { ...fallbackCampaignConfig.trigger, ...config.trigger },
    form: { ...fallbackCampaignConfig.form, ...config.form },
    plugins: {
      ...fallbackCampaignConfig.plugins,
      ...config.plugins,
      customCursor: {
        active: false,
        ...config.plugins?.customCursor,
      },
      announcementBar: {
        id: 'announcement-1',
        active: false,
        title: 'Limited offer',
        subtitle: 'Unlock a special reward today.',
        backgroundColor: '#111827',
        backgroundEffect: 'soft',
        textColor: '#ffffff',
        placement: 'top',
        countdownEnabled: false,
        showCountdown: true,
        ...config.plugins?.announcementBar,
        alignment: config.plugins?.announcementBar?.alignment || 'center',
        behavior: config.plugins?.announcementBar?.behavior || 'fixed',
        ctaEnabled: Boolean(config.plugins?.announcementBar?.ctaEnabled),
        ctaText: config.plugins?.announcementBar?.ctaText || 'Shop now',
        ctaTextColor: config.plugins?.announcementBar?.ctaTextColor || '#111827',
        ctaBackgroundColor: config.plugins?.announcementBar?.ctaBackgroundColor || '#ffffff',
        ctaCornerRadius: config.plugins?.announcementBar?.ctaCornerRadius ?? 50,
        ctaBorderSize: config.plugins?.announcementBar?.ctaBorderSize ?? 0,
        ctaBorderColor: config.plugins?.announcementBar?.ctaBorderColor || '#ffffff',
        dismissEnabled: Boolean(config.plugins?.announcementBar?.dismissEnabled),
        dismissOnce: Boolean(config.plugins?.announcementBar?.dismissOnce),
      },
      announcementBars: (config.plugins?.announcementBars?.length
        ? config.plugins.announcementBars
        : config.plugins?.announcementBar
          ? [config.plugins.announcementBar]
          : []
      ).map((bar, index) => ({
        ...bar,
        id: bar.id || `announcement-${index + 1}`,
        active: Boolean(bar.active),
        title: bar.title || 'Limited offer',
        subtitle: bar.subtitle || 'Unlock a special reward today.',
        backgroundColor: bar.backgroundColor || '#111827',
        backgroundEffect:
          bar.backgroundEffect === 'none' || bar.backgroundEffect === 'spotlight'
            ? bar.backgroundEffect
            : 'soft',
        textColor: bar.textColor || '#ffffff',
        placement: bar.placement || 'top',
        countdownEnabled: Boolean(bar.countdownEnabled),
        showCountdown: bar.showCountdown !== false,
        alignment: bar.alignment || 'center',
        behavior: bar.behavior || 'fixed',
        ctaEnabled: Boolean(bar.ctaEnabled),
        ctaText: bar.ctaText || 'Shop now',
        ctaTextColor: bar.ctaTextColor || '#111827',
        ctaBackgroundColor: bar.ctaBackgroundColor || '#ffffff',
        ctaCornerRadius: bar.ctaCornerRadius ?? 50,
        ctaBorderSize: bar.ctaBorderSize ?? 0,
        ctaBorderColor: bar.ctaBorderColor || '#ffffff',
        dismissEnabled: Boolean(bar.dismissEnabled),
        dismissOnce: Boolean(bar.dismissOnce),
      })),
      announcementBarSettings: {
        allowMultiple: Boolean(config.plugins?.announcementBarSettings?.allowMultiple),
        displayMode:
          config.plugins?.announcementBarSettings?.displayMode === 'alternate'
            ? 'alternate'
            : 'stacked',
        rotationSeconds: Math.min(
          120,
          Math.max(2, Number(config.plugins?.announcementBarSettings?.rotationSeconds) || 6),
        ),
        transition:
          config.plugins?.announcementBarSettings?.transition === 'none' ||
          config.plugins?.announcementBarSettings?.transition === 'fade'
            ? config.plugins.announcementBarSettings.transition
            : 'slide',
      },
      currencyConverter: {
        active: false,
        baseCurrency: 'VND',
        targetCurrencies: ['USD', 'EUR', 'JPY', 'INR', 'CNY', 'IDR', 'THB', 'KRW', 'MYR'],
        autoConvertByIp: true,
        showCurrencySwitcher: false,
        showSwitcherOnlyOutsideVietnam: false,
        switcherPosition: 'bottom-right',
        switcherPrimaryColor: '#0f766e',
        showCurrencySymbol: true,
        showCurrencyCode: true,
        rates: {
          USD: 0.000039,
          EUR: 0.000036,
          JPY: 0.0061,
          INR: 0.0033,
          CNY: 0.00028,
          IDR: 0.62,
          THB: 0.00135,
          KRW: 0.052,
          MYR: 0.00016,
        },
        ...config.plugins?.currencyConverter,
      },
      shoppableVideos: {
        id: 'shoppable-1',
        active: false,
        title: 'Watch and buy',
        pageUrl: '/',
        cardCornerRadius: 20,
        cardBorderSize: 0,
        cardBorderColor: '#111827',
        cardPlaceholderColor: '#111827',
        productBarPrimaryColor: '#000000',
        productBarSecondaryColor: '#4b5563',
        productBarBackgroundColor: '#ffffff',
        items: [],
        carousels: [],
        activeCarouselId: '',
        ...config.plugins?.shoppableVideos,
        placement: {
          preset: 'after-hero',
          customSelector: '',
          customMode: 'after',
          ...config.plugins?.shoppableVideos?.placement,
        },
      },
      productCountdown: {
        items: config.plugins?.productCountdown?.items || [],
        activeItemId: config.plugins?.productCountdown?.activeItemId || '',
      },
    },
  };
}
